import { AccessHoursPolicy } from './access-hours.model'
import { StationType, Language, SessionDuration } from '@app/shared/app.enums'
import { Club } from './club.model'
import { Role } from './role.model'
import { User } from './user.model'

export const SESSION_DURATIONS: Array<[SessionDuration, string]> = [
  [SessionDuration.FIVE_MINUTES, '5m'],
  [SessionDuration.FIFTEEN_MINUTES, '15m'],
  [SessionDuration.THIRTY_MINUTES, '30m'],
  [SessionDuration.TWO_HOURS, '2h'],
  [SessionDuration.FOUR_HOURS, '4h'],
]

// Keyera Web Link (YB-2007): lifetime of a linked browser session (QR handoff).
// Unrelated to SESSION_DURATIONS (the C-Portal login session). Must stay in sync
// with PAPI's KEYERA_WEB_SESSION_DURATION_TYPES.
export const KEYERA_WEB_SESSION_DURATIONS: string[] = ['1h', '12h', '1d', '7d', '30d']

export type ShiftAndBillingType = 'regular' | 'holiday'

export type CLIENT_ID = string | number

export type ModulesType = 'SportsAndCulture' | 'Timewatch' | 'Visitors' | 'Residential' | 'Kindergarten' | 'Authorized' | 'Delivery' | 'Nursing' | 'Courses'

/**
 * Modules no active client uses (on prod the last Kindergarten scan was 12/2024, the last Nursing
 * scan 02/2025). The module switcher and the Roles page leave them out (QA 14/10/2026). A client
 * record keeps them, so the Manage Clients form still lists them and an edit never drops one.
 */
export const RETIRED_MODULES: readonly ModulesType[] = ['Kindergarten', 'Nursing']

export class Client {
  id: CLIENT_ID
  name: string
  number: string
  api_token: string
  field: string
  country: string
  address: string
  modules: Array<ModulesType>
  employees: string
  contact: string
  mobile: string
  email: string
  country_code?: string
  language: Language
  event_default_password: string
  default_session_duration: string
  keyera_web_session_duration: string
  default_station_type: StationType
  event_default_club: number
  image: string
  events: [Event]
  users: [User]
  roles: [Role]
  locations: [Location]
  clubs: [Club]
  app_name: string
  app_link: string
  two_factor_authentication: boolean
  pass_create_pass?: boolean = false
  time_zone: number = NaN
  // SAML fields
  slug?: string // Organization slug for URL (e.g., "acme-corp")
  saml_enabled?: boolean = false
  saml_entity_id?: string = ''
  saml_idp_url?: string = ''
  saml_cert?: string = ''
  // Time-based access control
  access_hours_policy?: string | null = null
  // IP whitelist
  ip_whitelist_enabled?: boolean = false
  ip_whitelist?: string[] = []
  // Client limits
  client_limits?: ClientLimits
}

export class CreateClientInput {
  name: string = ''
  number: string = ''
  field: string = ''
  country: string = ''
  address: string = ''
  employees: string = ''
  contact: string = ''
  mobile: string = ''
  email: string = ''
  country_code?: string = ''
  image?: string = ''
  language: Language = Language.ENGLISH
  default_station_type: string = StationType.front_camera
  event_default_password: string = ''
  default_session_duration: string = ''
  event_default_club: number = NaN
  modules?: Array<ModulesType> = []
  two_factor_authentication: boolean = false
  pass_create_pass?: boolean = false
  time_zone: number = 2
  admin_user_name: string = ''
  admin_mobile: string = ''
  admin_password: string = ''
}

export class UpdateClientInput {
  id: CLIENT_ID = NaN
  name?: string = ''
  number?: string = ''
  field?: string = ''
  country?: string = ''
  address?: string = ''
  employees?: string = ''
  contact?: string = ''
  language: Language = Language.ENGLISH
  default_station_type: string = StationType.front_camera
  mobile?: string = ''
  country_code?: string = ''
  email?: string = ''
  image?: string = ''
  event_default_password?: string = ''
  default_session_duration?: string = ''
  keyera_web_session_duration?: string = ''
  event_default_club?: number = NaN
  two_factor_authentication: boolean = false
  pass_create_pass?: boolean = false
  time_zone: number = NaN
  client_limits?: Partial<ClientLimits> = {}
  access_hours_policy?: AccessHoursPolicy | null = null // JSON field, see AccessHoursPolicy
}

export class CreateShiftAndBillingInput {
  name: string = ''
  type: ShiftAndBillingType = 'regular'
  days_start: number = NaN
  days_end: number = NaN
  hours_start?: string | null = null
  hours_end?: string | null = null
  fee: number = NaN
  /*   addition_rate?: number = NaN;
  addition_rate_percentage?: number = NaN; */
}

export class UpdateShiftsAndBillingInput extends CreateShiftAndBillingInput {
  id: string | number = ''
}

// Client Limits Models
export interface LimitUsageItem {
  limit?: number
  currentUsage: number
  utilizationPercentage?: number
  isApproaching: boolean
  isExceeded: boolean
  status: 'OK' | 'APPROACHING' | 'REACHED' | 'EXCEEDED'
}

export interface EntranceLimitUsage extends LimitUsageItem {
  totalMonthlyLimit?: number
  currentMonthUsage?: number
  stationBreakdown?: StationEntranceUsage[]
}

export interface StationEntranceUsage {
  stationId: number
  stationName: string
  currentMonthCount: number
  limit?: number
  isExceeded: boolean
}

export interface ClientLimitsAndUsage {
  clientId: number
  clientName?: string
  events: LimitUsageItem
  stations: LimitUsageItem
  users: LimitUsageItem
  requirements: LimitUsageItem
  employeePasses: LimitUsageItem
  authorizedPasses: LimitUsageItem
  vehiclePasses: LimitUsageItem
  hmePasses: LimitUsageItem
  entrances: EntranceLimitUsage
  passesPerEvent: LimitUsageItem
  areasPerEvent: LimitUsageItem
  entrancesPerEvent?: LimitUsageItem
  stationsWithCallGroups: LimitUsageItem
  // Room booking (YB-2066 / YB-2074), surfaced by YB-2121
  bookableAreas?: LimitUsageItem
  bookingsPerMonth?: LimitUsageItem
  bookingGuestPassesPerMonth?: LimitUsageItem
  hasExceededLimits: boolean
  hasApproachingLimits: boolean
  overallStatus: 'OK' | 'APPROACHING' | 'REACHED' | 'EXCEEDED'
}

export interface EntranceStatsResponse {
  clientId: number
  totalCurrentMonthCount: number
  totalMonthlyLimit?: number
  totalLimitExceeded: boolean
  stationStats: StationEntranceUsage[]
}

export interface EntranceLimitStatusResponse {
  success: boolean
  errorText?: string
  currentCount: number
  maxAllowed?: number
  limitType: string
  isExceeded: boolean
}

// Client Limits Classes
export class ClientLimits {
  max_events?: number
  max_stations?: number
  max_users?: number
  max_passes_per_event?: number
  max_entrances_per_event?: number
  max_areas_per_event?: number
  max_employee_passes?: number
  max_authorized_passes?: number
  max_vehicle_passes?: number
  max_hme_passes?: number
  max_entrances_per_station_per_month?: number
  max_stations_with_call_groups?: number
  max_requirements?: number
  vehicle_hme_daily_reset?: boolean
  /** YB-2066 / YB-2074 room booking. Empty means no limit, like every other field here. */
  max_bookable_areas?: number
  max_bookings_per_month?: number
  max_booking_guest_passes_per_month?: number
}

// Input types for get_clients query
export interface PaginationInput {
  page?: number
  limit?: number
}

export interface ClientFiltersInput {
  name?: string
  email?: string
  mobile?: string
  country?: string
  field?: string
  contact?: string
}

export interface GetClientsInput {
  pagination?: PaginationInput
  filters?: ClientFiltersInput
}

export interface ClientsOutput {
  success: boolean
  errorText?: string
  clients: Client[]
  pagination: {
    currentPage: number
    totalPages: number
    totalCount: number
    itemsPerPage: number
    hasNextPage: boolean
    hasPreviousPage: boolean
  }
}
